/**
 * <quantity-input>
 *
 * Dawn-style custom element wrapper around the +/- quantity selector.
 *
 * Purpose:
 *  - Geeft apps een stabiele selector (`quantity-input`, zelfde naam als in
 *    Dawn) zodat ze de quantity stepper kunnen vinden en mee kunnen luisteren.
 *  - Exposeert een kleine API (`value` getter/setter, `validate()`) en
 *    dispatcht `change` + `quantity:change` events op de wrapper.
 *  - De feitelijke +/- klik-logica blijft in <product-info> (zie
 *    product-info.js > initQuantitySelector); deze wrapper laat dat zoals het
 *    is om gedrag identiek te houden, maar zorgt dat events bubbelen tot op
 *    de wrapper zodat externe apps zoals subscription/quantity-break apps
 *    netjes kunnen reageren.
 *
 * Markup: zie snippets/quantity-selector.liquid
 *   <quantity-input data-quantity-selector data-section-id='…'>
 *     <button data-quantity-decrease>−</button>
 *     <input  data-quantity-input  name='quantity' …>
 *     <button data-quantity-increase>+</button>
 *   </quantity-input>
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this._onInputChange = this._onInputChange.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-quantity-input]');
    if (!this.input) return;
    this.input.addEventListener('change', this._onInputChange);
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('change', this._onInputChange);
  }

  /** Current numeric value (defaults to 1 if missing/invalid). */
  get value() {
    if (!this.input) return 1;
    const v = parseInt(this.input.value, 10);
    return isNaN(v) ? 1 : v;
  }

  set value(next) {
    if (!this.input) return;
    const numeric = Math.max(1, parseInt(next, 10) || 1);
    if (this.input.value === String(numeric)) return;
    this.input.value = String(numeric);
    this._notify();
  }

  /** Returns the parsed max-quantity (from data-max-quantity), or null. */
  get max() {
    if (!this.input) return null;
    const raw = this.input.getAttribute('data-max-quantity');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  _onInputChange() {
    this._notify();
  }

  _notify() {
    this.dispatchEvent(
      new CustomEvent('quantity:change', {
        bubbles: true,
        detail: { value: this.value, max: this.max, sectionId: this.dataset.sectionId },
      })
    );
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}
